import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Button, IconButton, Paper, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';

import { wallStorage } from '@walls/services/wallsApi';
import {
  useWallParticipantsStore,
  useWallStore,
  useWorkspaceUiStore,
  type ParticipantId,
  type GroupId,
  type WallBoard,
  type WallGroup,
} from '@walls/store';
import {
  getPanelColor,
  getPanelTitleColor,
  getYellowActionColor,
  getYellowActionTextColor,
} from '../theme/wallThemeStyles';
import { ParticipantsList } from './ParticipantsList';
import { GroupsList } from './GroupsList';
import { GroupEditorOverlay } from './GroupEditorOverlay';
import { putCurrentParticipantFirst } from './participantsPanelUtils';

interface ParticipantsPanelProps {
  board: WallBoard;
}

interface PanelPosition {
  x: number;
  y: number;
}

interface PanelDrag {
  offsetX: number;
  offsetY: number;
  panelHeight: number;
  panelWidth: number;
  pointerId: number;
}

const panelWidth = 340;
const initialPanelTop = 80;
const initialPanelRight = 20;
const panelViewportMargin = 8;

export function ParticipantsPanel({ board }: ParticipantsPanelProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<PanelDrag | null>(null);
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(null);
  const topicKey = useWallStore((state) => state.topicKey);
  const theme = useWallStore((state) => state.theme);
  const participants = useWallParticipantsStore((state) => state.participants);
  const groups = useWallParticipantsStore((state) => state.groups);
  const currentParticipantId = useWallParticipantsStore((state) => state.currentParticipantId);
  const deleteParticipant = useWallParticipantsStore((state) => state.deleteParticipant);
  const createGroup = useWallParticipantsStore((state) => state.createGroup);
  const deleteGroup = useWallParticipantsStore((state) => state.deleteGroup);
  const updateGroup = useWallParticipantsStore((state) => state.updateGroup);
  const visibleParticipantIds = useWorkspaceUiStore((state) => state.visibleParticipantIds);
  const toggleParticipantPanel = useWorkspaceUiStore((state) => state.toggleParticipantPanel);
  const toggleVisibleParticipant = useWorkspaceUiStore((state) => state.toggleVisibleParticipant);
  const toggleVisibleParticipants = useWorkspaceUiStore((state) => state.toggleVisibleParticipants);
  const [activeTab, setActiveTab] = useState<'participants' | 'groups'>('participants');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [creatingGroupName, setCreatingGroupName] = useState('');
  const [creatingParticipantIds, setCreatingParticipantIds] = useState<ParticipantId[]>([]);
  const [editingGroupId, setEditingGroupId] = useState<GroupId | null>(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [editingParticipantIds, setEditingParticipantIds] = useState<ParticipantId[]>([]);
  const isFilteringCards = visibleParticipantIds.length > 0;
  const isBoardOwner = Boolean(board.isOwner);
  const storedParticipantName = wallStorage.getUserName();
  const canShowGroupsTab = isBoardOwner || groups.length > 0;
  const orderedParticipants = useMemo(
    () => putCurrentParticipantFirst(participants, currentParticipantId, storedParticipantName),
    [currentParticipantId, participants, storedParticipantName]
  );
  const editingGroup = useMemo(
    () => groups.find((group) => group.id === editingGroupId) ?? null,
    [editingGroupId, groups]
  );
  const showGroupsFooter = activeTab === 'groups' && isBoardOwner;

  useEffect(() => {
    if (activeTab === 'groups' && !canShowGroupsTab) {
      setActiveTab('participants');
    }
  }, [activeTab, canShowGroupsTab]);

  const startPanelDrag = (event: PointerEvent<HTMLElement>) => {
    const panel = panelRef.current;

    if (!panel) {
      return;
    }

    const rect = panel.getBoundingClientRect();

    dragRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      panelHeight: rect.height,
      panelWidth: rect.width,
      pointerId: event.pointerId,
    };

    setPanelPosition({ x: rect.left, y: rect.top });
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const movePanel = (event: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;

    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    setPanelPosition(
      getClampedPanelPosition({
        panelHeight: drag.panelHeight,
        panelWidth: drag.panelWidth,
        x: event.clientX - drag.offsetX,
        y: event.clientY - drag.offsetY,
      })
    );
    event.preventDefault();
  };

  const stopPanelDrag = (event: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;

    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    dragRef.current = null;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const startCreatingGroup = () => {
    setEditingGroupId(null);
    setIsCreatingGroup(true);
    setCreatingGroupName('');
    setCreatingParticipantIds([]);
  };

  const cancelCreatingGroup = () => {
    setIsCreatingGroup(false);
    setCreatingGroupName('');
    setCreatingParticipantIds([]);
  };

  const submitNewGroup = async () => {
    const trimmedName = creatingGroupName.trim();

    if (!trimmedName) {
      return;
    }

    await createGroup(board.id, trimmedName, creatingParticipantIds, topicKey);
    cancelCreatingGroup();
  };

  const startEditingGroup = (group: WallGroup) => {
    setIsCreatingGroup(false);
    setEditingGroupId(group.id);
    setEditingGroupName(group.name);
    setEditingParticipantIds(group.participantIds);
  };

  const cancelEditingGroup = () => {
    setEditingGroupId(null);
    setEditingGroupName('');
    setEditingParticipantIds([]);
  };

  const submitGroup = async (group: WallGroup) => {
    const trimmedName = editingGroupName.trim();

    if (!trimmedName) {
      return;
    }

    await updateGroup(board.id, group, trimmedName, editingParticipantIds, topicKey);
    cancelEditingGroup();
  };

  const toggleEditingParticipant = (participantId: ParticipantId) => {
    setEditingParticipantIds((currentParticipantIds) =>
      currentParticipantIds.includes(participantId)
        ? currentParticipantIds.filter((id) => id !== participantId)
        : [...currentParticipantIds, participantId]
    );
  };

  const toggleCreatingParticipant = (participantId: ParticipantId) => {
    setCreatingParticipantIds((currentParticipantIds) =>
      currentParticipantIds.includes(participantId)
        ? currentParticipantIds.filter((id) => id !== participantId)
        : [...currentParticipantIds, participantId]
    );
  };

  return (
    <Paper
      ref={panelRef}
      elevation={0}
      sx={{
        borderRadius: '8px',
        bgcolor: getPanelColor(theme),
        boxShadow: '0px 3px 6px #00000029',
        display: 'flex',
        flexDirection: 'column',
        height: '75vh',
        overflow: 'hidden',
        position: 'fixed',
        right: panelPosition ? 'auto' : initialPanelRight,
        top: panelPosition ? panelPosition.y : initialPanelTop,
        left: panelPosition ? panelPosition.x : 'auto',
        width: panelWidth,
        zIndex: 13,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: getPanelColor(theme),
          borderBottom: '1px solid #e5e7eb',
          color: getPanelTitleColor(theme),
          display: 'flex',
        }}
      >
        {canShowGroupsTab ? (
          <Tabs
            value={activeTab}
            onChange={(_event, value: 'participants' | 'groups') => setActiveTab(value)}
            variant="fullWidth"
            sx={{
              flex: '1 1 auto',
              minHeight: 48,
              '& .MuiTab-root': {
                color: getPanelTitleColor(theme),
                minHeight: 48,
                textTransform: 'none',
              },
              '& .Mui-selected': {
                color: getPanelTitleColor(theme),
              },
            }}
          >
            <Tab label="Participants" value="participants" />
            <Tab label="Groups" value="groups" />
          </Tabs>
        ) : (
          <Typography
            component="h2"
            sx={{
              flex: '1 1 auto',
              color: getPanelTitleColor(theme),
              fontSize: '0.875rem',
              fontWeight: 500,
              lineHeight: '48px',
              px: 2,
            }}
          >
            Participants
          </Typography>
        )}
        <IconButton
          size="small"
          aria-label="Drag participants panel"
          onPointerDown={startPanelDrag}
          onPointerMove={movePanel}
          onPointerUp={stopPanelDrag}
          onPointerCancel={stopPanelDrag}
          sx={{
            color: getPanelTitleColor(theme),
            cursor: 'grab',
            flex: '0 0 auto',
            mx: 0.75,
            touchAction: 'none',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label="Close participants panel"
          onClick={toggleParticipantPanel}
          sx={{
            color: getPanelTitleColor(theme),
            flex: '0 0 auto',
            mr: 0.75,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          bgcolor: getPanelColor(theme),
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        {activeTab === 'participants' && (
          <ParticipantsList
            canManageParticipants={isBoardOwner}
            currentParticipantId={currentParticipantId}
            isFilteringCards={isFilteringCards}
            participants={orderedParticipants}
            groups={groups}
            visibleParticipantIds={visibleParticipantIds}
            onDeleteParticipant={(participantId) =>
              void deleteParticipant(board.id, participantId, topicKey)
            }
            onToggleVisibleParticipant={toggleVisibleParticipant}
          />
        )}
        {activeTab === 'groups' && canShowGroupsTab && (
          <GroupsList
            canManageGroups={isBoardOwner}
            participants={participants}
            groups={groups}
            visibleParticipantIds={visibleParticipantIds}
            onDeleteGroup={(groupId) => void deleteGroup(board.id, groupId, topicKey)}
            onEditGroup={startEditingGroup}
            onToggleVisibleParticipants={toggleVisibleParticipants}
          />
        )}
      </Box>
      {showGroupsFooter && (
        <Box
          sx={{
            bgcolor: getPanelColor(theme),
            borderTop: '1px solid #e5e7eb',
            px: 1.5,
            py: 1,
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={startCreatingGroup}
            sx={{
              bgcolor: getYellowActionColor(theme),
              color: getYellowActionTextColor(theme),
              py: 0.5,
              textTransform: 'none',
              borderRadius: '4px',
              width: '100%',
              '&:hover': { bgcolor: getYellowActionColor(theme) },
            }}
          >
            Add a group
          </Button>
        </Box>
      )}
      {isBoardOwner && isCreatingGroup && (
        <GroupEditorOverlay
          editingParticipantIds={creatingParticipantIds}
          participants={participants}
          saveLabel="Create"
          groupName={creatingGroupName}
          title="Add group"
          onCancel={cancelCreatingGroup}
          onSave={() => void submitNewGroup()}
          onGroupNameChange={setCreatingGroupName}
          onToggleParticipant={toggleCreatingParticipant}
        />
      )}
      {isBoardOwner && editingGroup && (
        <GroupEditorOverlay
          editingParticipantIds={editingParticipantIds}
          participants={participants}
          group={editingGroup}
          groupName={editingGroupName}
          onCancel={cancelEditingGroup}
          onSave={() => void submitGroup(editingGroup)}
          onGroupNameChange={setEditingGroupName}
          onToggleParticipant={toggleEditingParticipant}
        />
      )}
    </Paper>
  );
}

function getClampedPanelPosition({
  panelHeight,
  panelWidth: draggedPanelWidth,
  x,
  y,
}: {
  panelHeight: number;
  panelWidth: number;
  x: number;
  y: number;
}): PanelPosition {
  const maxX = Math.max(
    panelViewportMargin,
    window.innerWidth - draggedPanelWidth - panelViewportMargin
  );
  const maxY = Math.max(
    panelViewportMargin,
    window.innerHeight - panelHeight - panelViewportMargin
  );

  return {
    x: Math.min(Math.max(panelViewportMargin, x), maxX),
    y: Math.min(Math.max(panelViewportMargin, y), maxY),
  };
}
